import { Box } from "@mui/material";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useEffect, useRef, useState, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import { Footer } from "./components/common/Footer/Footer";
import { Header } from "./components/common/Header/Header";
import MainContent from "./components/MainContent/MainContent";
import SideDrawer from "./components/SideDrawer/SideDrawer";
import { USER_ACCESS_MENU } from "./constants/sidebarMenu";
import SupportButton from "./CommonComponents/Support/SupportButton";
import SupportTicketDialog from "./CommonComponents/Support/SupportTicketDialog";
import HelpButton from "./CommonComponents/HelpSection/HelpButton";
import RefreshButton from "./CommonComponents/Refresh/RefreshButton";
import { fetchUserAccess } from "./services/OutboundOrders/UserRolesServices";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { setMsalInstance } from "./utils/fetch";
import { UserRolesProvider } from "./auth/UserRolesContext";
import { ApplicationContext } from "./context/ApplicationContext";

const theme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {

          '&.Mui-focused .MuiOutlinedInput-notchedOutline ': {
            borderColor: '#000',  // Your desired border color
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            borderColor: '#000',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#000',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#000',
          },
        },
      },
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        item: {
          "&.Mui-selected": {
            backgroundColor: '#000',
          },
          "&.Mui-selected:hover": {
            backgroundColor: '#797979'
          }
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: '#797979'
        },
      },
    },

    MuiContainer: {
      styleOverrides: {
        root: { width: '100%', maxWidth: 'none', padding: 0 },
      },
    },


  },
});

function App() {
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [openSupport, setOpenSupport] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [userRolesLoading, setUserRolesLoading] = useState(true);
  const location = useLocation();
  const isUserRolesPage = location.pathname.startsWith("/user-roles") || location.pathname.startsWith("/bottler-admin");
  const currentPage = location.pathname.split("/")[2];
  const { instance, inProgress, accounts } = useMsal();
  const [selectedApplication, setSelectedApplication] = useState("");

  useEffect(() => {
    setSelectedApplication("");
  }, [location.pathname]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None && accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
      setMsalInstance(instance);
    }
  }, [instance, accounts, inProgress]);

  // Set the document title
  useEffect(() => {
    document.title = "Autonomous Layer Root";
  }, []);

  // Measure header height for sidebar positioning (adapted from coke-ct-ui)
  useLayoutEffect(() => {
    const measure = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        if (height > 0) {
          setHeaderHeight(height);
        }
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const constraintMenuItems = [
    {
      id: 1,
      path: "/landing-page",
      title: "Landing",
    }
  ];

  // FETCH ROLES ON APP LOAD
  useEffect(() => {
    const loadUserRoles = async () => {
      try {
        const userAccessData = await fetchUserAccess();
        const roles =
          (userAccessData?.ssaAppRoles || []).map(r =>
            r.trim().toUpperCase()
          );
        setUserRoles(roles);
      } catch (e) {
        console.error("Failed to load roles", e);
        setUserRoles([]);
      } finally {
        setUserRolesLoading(false);
      }
    };
    loadUserRoles();
  }, []);

  return (
    // (isAuthenticated && accounts?.[0] && !loaderFlag) ?
    <Box
      sx={{
        margin: "auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {/* <PreventBackNavigation /> */}
      <ThemeProvider theme={theme}>
        <ApplicationContext.Provider
          value={{
            selectedApplication,
            setSelectedApplication,
          }}
        >
          <UserRolesProvider roles={userRoles} loading={userRolesLoading}>
            <Header
              headerRef={headerRef}
              title="AL App Root"
              handleLogout={() => { }}
              handleMenuItemClick={() => { }}
              storedRoles=""
            />
            {isUserRolesPage && (
              <>
                <RefreshButton headerHeight={headerHeight} />
                <SideDrawer
                  options={USER_ACCESS_MENU}
                  contentHeight={headerHeight}
                  flat
                  roles={userRoles} />
                <SupportButton onClick={() => setOpenSupport(true)} />
                <SupportTicketDialog
                  open={openSupport}
                  onClose={() => setOpenSupport(false)}
                  selectedApplication={selectedApplication}
                />
                <HelpButton currentPage={currentPage} />
              </>
            )}
            <Box
              sx={{
                flex: 1,
                marginLeft: isUserRolesPage ? "75px" : 0,
                overflowX: isUserRolesPage ? "hidden" : "undefined",
                overflowY: "auto",
                paddingLeft: isUserRolesPage ? 0 : "10px",
              }}
            >
              <MainContent />
            </Box>
            <Footer />
            <ToastContainer />
          </UserRolesProvider>
        </ApplicationContext.Provider>
      </ThemeProvider>
    </Box>

  );
}

export default App;
